import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getPipsExpectedResults, runGenerateStudyReport } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import {
  getResultNameTypesByOrchestrations,
  OrchestrationInference,
  ResultNameOrchestrations,
} from 'interfaces/calculateFeatures';
import { PipsResultsConfig } from 'interfaces/inferenceArtifacts';
import { StudyReportJob } from 'interfaces/job';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';
import { first, flatMap, get, indexOf, isEmpty, isEqual, keyBy, map, size } from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React, ReactElement, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import * as yup from 'yup';
import { ActionStepper } from '../ActionStepper';
import { JobWithRebuild } from '../JobWithRebuild';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import {
  findByOrchestrationResultArtifactUrlPattern,
  findOrchestrationByArtifactUrlPattern,
} from '../PostProcessingAndCalculateFeatures/utils';
import { getMapArtifactToSlides, JobNameAndDescriptionStep } from '../utils';
import { AdditionalParametersStep } from './AdditionalParametersStep';
import { PlotsStep } from './PlotsStep';
import { createAnalysisStudioOrder, GenerateStudyReportFormValues, GenerateStudyReportStep } from './types';
import { changeStainTypeAndMarkers } from './utils';

const SNACK_BAR_KEY_GENERATE_STUDY_REPORT = 'GENERATE_STUDY_REPORT';

export interface GenerateStudyReportProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  jobId?: string;
}

const defaultValues: GenerateStudyReportFormValues = {
  jobName: '',
  jobDescription: '',
  configParams: {
    pipsCellsTablesArtifactUrls: [],
    markers: [],
    manifest: [],
    configurablePlots: [],
  },
};

const validationSchema = {
  [GenerateStudyReportStep.NameAndDescription]: yup.object({}),
  [GenerateStudyReportStep.SelectInferenceModels]: yup.object().shape({
    configParams: yup.object().shape({
      pipsCellsTablesArtifactUrls: yup
        .array()
        .of(
          yup.object().shape({
            artifactUrlPattern: yup.string().required('Artifact URL Pattern is required'),
            slideIds: yup
              .array()
              .of(yup.string().required('Slide ID is required'))
              .min(1, 'At least one Slide ID is required'),
          })
        )
        .min(1, 'At least one Artifact URL Pattern is required')
        .max(1, 'Only one Artifact URL Pattern can be selected')
        .required('Artifact URL Pattern is required'),
    }),
  }),
  [GenerateStudyReportStep.AdditionalParameters]: yup.object({
    configParams: yup.object().shape({
      markers: yup
        .array()
        .of(yup.string().required('Channel name is required'))
        .min(1, 'At least one channel must be selected')
        .required('channels are required'),
    }),
  }),
  [GenerateStudyReportStep.AnalysisPlots]: yup.object({
    configParams: yup.object().shape({
      configurable_plots: yup
        .array()
        .of(
          yup.object().shape({
            plotName: yup.string().required('Plot name is required'),
            plotList: yup
              .array()
              .of(
                yup.object().shape({
                  plotType: yup.string().required('Plot type is required'),
                  column: yup.string().when('plotType', {
                    is: (val: string) => ['histogram', 'pie'].includes(val),
                    then: yup.string().required('Feature is required'),
                    otherwise: yup.string().notRequired(),
                  }),
                  columns: yup
                    .array()
                    .of(yup.string())
                    .when('plotType', {
                      is: (val: string) => ['box', 'violin'].includes(val),
                      then: yup
                        .array()
                        .of(yup.string())
                        .min(1, 'At least one feature is required')
                        .required('At least one feature is required'),
                      otherwise: yup.array().notRequired(),
                    }),
                  xAxis: yup.string().when('plotType', {
                    is: (val: string) => val === 'scatter',
                    then: yup.string().required('X Axis feature is required'),
                    otherwise: yup.string().notRequired(),
                  }),
                  yAxis: yup.string().when('plotType', {
                    is: (val: string) => val === 'scatter',
                    then: yup.string().required('Y Axis feature is required'),
                    otherwise: yup.string().notRequired(),
                  }),
                  bins: yup.number().when('plotType', {
                    is: (val: string) => val === 'histogram',
                    then: yup
                      .number()
                      .typeError('Bins must be a number')
                      .integer('Bins must be an integer')
                      .min(1, 'Bins must be at least 1')
                      .notRequired()
                      .nullable(),
                    otherwise: yup.number().notRequired(),
                  }),
                  group: yup.string().when('plotType', {
                    is: (val: string) => ['box'].includes(val),
                    then: yup.string().notRequired(),
                    otherwise: yup.string().notRequired(),
                  }),
                  includeTrendline: yup.boolean().when('plotType', {
                    is: (val: string) => val === 'scatter',
                    then: yup.boolean().notRequired().nullable(),
                    otherwise: yup.boolean().notRequired(),
                  }),
                })
              )
              .min(1, 'At least one plot must be added')
              .required('At least one plot must be added'),
          })
        )
        .min(1, 'At least one features analysis plot must be added'),
    }),
  }),
};

const buildSelectedPipsOrchestrationsFromJob = (
  pipsCellsTablesArtifactUrls: MapArtifactToSlides[],
  pipsResultsArtifacts: ResultNameOrchestrations[]
) => {
  const selectedPipsArtifacts: {
    [slideId: string]: {
      [resultName: string]: {
        orchestration: OrchestrationInference;
        resultName: string;
        resultType: string;
      };
    };
  } = {};

  pipsCellsTablesArtifactUrls.forEach(({ artifactUrlPattern, slideIds }) => {
    const resultGroup = findByOrchestrationResultArtifactUrlPattern(pipsResultsArtifacts, artifactUrlPattern);
    const orchestration = findOrchestrationByArtifactUrlPattern(resultGroup?.orchestrations, artifactUrlPattern);

    if (!resultGroup || !orchestration) {
      return;
    }

    slideIds.forEach((slideId) => {
      selectedPipsArtifacts[slideId] = {
        ...selectedPipsArtifacts[slideId],
        [`${resultGroup.resultName}`]: {
          orchestration,
          resultName: resultGroup.resultName,
          resultType: resultGroup.resultType,
        },
      };
    });
  });

  return selectedPipsArtifacts;
};

export const GenerateStudyReport = (props: GenerateStudyReportProps): ReactElement => {
  const { onClose, casesParams, jobId } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const studyId = casesParams.filters?.studyId;
  const orchestrationSelectionState = useOrchestrationSelections();
  const { selectedPipsOrchestrations, setSelectedPipsOrchestrations } = orchestrationSelectionState;
  const [selectedPipsOrchestrationId, setSelectedPipsOrchestrationId] = useState<string>(null);
  const [selectedStainType, setSelectedStainType] = useState<string>('');
  const [pendingPreviousJobPipsSelections, setPendingPreviousJobPipsSelections] = useState<
    MapArtifactToSlides[] | null
  >(null);
  const [preservePreviousJobMarkersOnNextSelectionSync, setPreservePreviousJobMarkersOnNextSelectionSync] =
    useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const [stepWarnings, setStepWarnings] = useState<Record<number, string>>({});

  const currentValidationSchema = validationSchema[createAnalysisStudioOrder[activeStep]];

  const methods = useForm<GenerateStudyReportFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const { data: pipsExpectedResults, isLoading: isPipsExpectedResultsLoading } = useQuery({
    queryKey: [
      'pipsExpectedResults',
      studyId,
      selectedPipsOrchestrationId,
      watch('configParams.manifest'),
      selectedStainType,
    ],
    queryFn: () =>
      getPipsExpectedResults({
        studyId,
        orchestrationId: selectedPipsOrchestrationId,
        slideIds: watch('configParams.manifest'),
        stainingType: selectedStainType,
      }),
    enabled:
      Boolean(studyId) &&
      Boolean(selectedPipsOrchestrationId) &&
      Boolean(selectedStainType) &&
      !isEmpty(watch('configParams.manifest')),
  });

  const {
    data: pipsResultsArtifacts,
    isLoading: isPipsResultsLoading,
    isFetching: isPipsResultsFetching,
    isError: isPipsResultsError,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: casesParams,
    config: PipsResultsConfig,
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    enabled: Boolean(studyId),
  });

  const slidesById = useMemo(() => keyBy(slides, 'slideId'), [slides]);
  const selectedInferenceArtifactUrlPatternsWithSlides = useMemo(
    () => getMapArtifactToSlides(map(slides, 'slideId'), selectedPipsOrchestrations),
    [selectedPipsOrchestrations, slides]
  );
  const selectedManifest = useMemo(
    () => flatMap(selectedInferenceArtifactUrlPatternsWithSlides, 'slideIds'),
    [selectedInferenceArtifactUrlPatternsWithSlides]
  );
  const selectedResultsStainType = useMemo(
    () => slidesById[first(selectedManifest)]?.stainingType || '',
    [selectedManifest, slidesById]
  );

  const createAnalysisStudioMutation = useMutationWithErrorSnackbar({
    mutationFn: runGenerateStudyReport,
    mutationDescription: 'generate study report',
    onSuccess: () => {
      enqueueSnackbar('Study report generation started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_GENERATE_STUDY_REPORT);
    },
  });

  useEffect(() => {
    if (pendingPreviousJobPipsSelections === null || isPipsResultsLoading || isPipsResultsFetching) {
      return;
    }

    setSelectedPipsOrchestrations(
      buildSelectedPipsOrchestrationsFromJob(pendingPreviousJobPipsSelections, pipsResultsArtifacts || [])
    );
    setPendingPreviousJobPipsSelections(null);
  }, [
    isPipsResultsFetching,
    isPipsResultsLoading,
    pendingPreviousJobPipsSelections,
    pipsResultsArtifacts,
    setSelectedPipsOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobPipsSelections !== null) {
      return;
    }
    const currentInferenceArtifactUrlPatternsWithSlides = getValues('configParams.pipsCellsTablesArtifactUrls');
    const currentManifest = getValues('configParams.manifest');
    const shouldUpdateInferenceArtifactUrlPatterns = !isEqual(
      currentInferenceArtifactUrlPatternsWithSlides,
      selectedInferenceArtifactUrlPatternsWithSlides
    );
    const shouldUpdateManifest = !isEqual(currentManifest, selectedManifest);

    if (!shouldUpdateInferenceArtifactUrlPatterns && !shouldUpdateManifest) {
      return;
    }

    if (shouldUpdateInferenceArtifactUrlPatterns) {
      setValue('configParams.pipsCellsTablesArtifactUrls', selectedInferenceArtifactUrlPatternsWithSlides);
    }

    if (shouldUpdateManifest) {
      setValue('configParams.manifest', selectedManifest);
    }

    trigger('configParams.pipsCellsTablesArtifactUrls');
    setWarnings(GenerateStudyReportStep.SelectInferenceModels, selectedManifest);
  }, [
    getValues,
    pendingPreviousJobPipsSelections,
    selectedInferenceArtifactUrlPatternsWithSlides,
    selectedManifest,
    setValue,
    trigger,
  ]);

  useEffect(() => {
    if (pendingPreviousJobPipsSelections !== null) {
      return;
    }

    if (preservePreviousJobMarkersOnNextSelectionSync) {
      if (selectedStainType !== selectedResultsStainType) {
        setSelectedStainType(selectedResultsStainType);
      }
      setPreservePreviousJobMarkersOnNextSelectionSync(false);
      return;
    }

    changeStainTypeAndMarkers(
      selectedResultsStainType,
      selectedStainType,
      getValues('configParams.markers') || [],
      setSelectedStainType,
      (markers) => setValue('configParams.markers', markers)
    );
  }, [
    getValues,
    pendingPreviousJobPipsSelections,
    preservePreviousJobMarkersOnNextSelectionSync,
    selectedResultsStainType,
    selectedStainType,
    setValue,
  ]);

  const checkValidationAndSetIsStepFailed = async (step: GenerateStudyReportStep) => {
    const stepIndex = indexOf(createAnalysisStudioOrder, step);
    let isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));

    return isValid;
  };

  const setWarnings = (step: GenerateStudyReportStep, manifest = watch('configParams.manifest')) => {
    const stepIndex = indexOf(createAnalysisStudioOrder, step);

    if (step === GenerateStudyReportStep.SelectInferenceModels) {
      const nextWarning =
        size(manifest) > 0 && size(manifest) < size(slides)
          ? "Some slides aren't included in the selected jobs, so they will not be analyzed."
          : '';

      setStepWarnings((prev) => {
        if (prev[stepIndex] === nextWarning) {
          return prev;
        }

        return {
          ...prev,
          [stepIndex]: nextWarning,
        };
      });
    }
  };

  const onSelectedJobParamChange = (newValue: StudyReportJob) => {
    if (isEmpty(newValue)) {
      setPendingPreviousJobPipsSelections(null);
      setPreservePreviousJobMarkersOnNextSelectionSync(false);
      setSelectedPipsOrchestrations({});
      setSelectedStainType('');
      reset(defaultValues);
    } else {
      setPendingPreviousJobPipsSelections(newValue?.params?.pipsCellsTablesArtifactUrls || []);
      setPreservePreviousJobMarkersOnNextSelectionSync(true);
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        configParams: newValue?.params,
      });
    }
  };
  const stepsMap = {
    [GenerateStudyReportStep.NameAndDescription]: {
      label: 'Job Name and Description',
      subLabel: activeStep !== 0 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    [GenerateStudyReportStep.SelectInferenceModels]: {
      label: 'Select Post Processing - Cell Typing',
      subLabel: activeStep !== 1 && `${size(watch('configParams.manifest'))}/${size(slides)} results selected`,
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {isPipsResultsError && (
              <Typography color="error">Error fetching Post Processing - Cell Typing results</Typography>
            )}
            {get(errors, 'configParams.pipsCellsTablesArtifactUrls') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.pipsCellsTablesArtifactUrls')?.message)}
              </Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={getResultNameTypesByOrchestrations(pipsResultsArtifacts)}
              pipsResultsArtifacts={pipsResultsArtifacts}
              displayInferenceModels={false}
              displayPipsResults
              isLoading={isPipsResultsLoading || isPipsResultsFetching || isSlidesFetching}
              {...orchestrationSelectionState}
              disableIfNoJob={true}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => {
        checkValidationAndSetIsStepFailed(GenerateStudyReportStep.SelectInferenceModels);
        setWarnings(GenerateStudyReportStep.SelectInferenceModels);
        const artifactUrlPattern = first(watch('configParams.pipsCellsTablesArtifactUrls'))?.artifactUrlPattern;
        const selectedPipsResult = findByOrchestrationResultArtifactUrlPattern(
          pipsResultsArtifacts,
          artifactUrlPattern
        );
        const currentOrchestration = findOrchestrationByArtifactUrlPattern(
          selectedPipsResult?.orchestrations,
          artifactUrlPattern
        );

        setSelectedPipsOrchestrationId(currentOrchestration?.orchestrationId);
      },
    },
    [GenerateStudyReportStep.AdditionalParameters]: {
      label: 'Additional Parameters',
      optional: true,
      content: (
        <AdditionalParametersStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(GenerateStudyReportStep.AdditionalParameters)
          }
          selectedStainType={selectedStainType}
          setSelectedStainType={setSelectedStainType}
          casesParams={casesParams}
        />
      ),
      onNextOrBackClick: () => {
        trigger('configParams.pipsCellsTablesArtifactUrls');
        checkValidationAndSetIsStepFailed(GenerateStudyReportStep.AdditionalParameters);
      },
    },
    [GenerateStudyReportStep.AnalysisPlots]: {
      label: 'Analysis Plots',
      optional: true,
      content: (
        <PlotsStep
          studyId={studyId}
          pipsExpectedResults={pipsExpectedResults}
          selectedPipsOrchestrationId={selectedPipsOrchestrationId}
          isPipsExpectedResultsLoading={isPipsExpectedResultsLoading}
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(GenerateStudyReportStep.AnalysisPlots)
          }
        />
      ),
      onNextOrBackClick: () => {
        checkValidationAndSetIsStepFailed(GenerateStudyReportStep.AnalysisPlots);
      },
    },
  };

  const steps = map(createAnalysisStudioOrder, (stepId) => stepsMap[stepId]);

  const onSubmit: SubmitHandler<any> = (data: GenerateStudyReportFormValues) => {
    createAnalysisStudioMutation.mutate({
      ...casesParams,
      ...data,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for study report generation to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_GENERATE_STUDY_REPORT,
      autoHideDuration: null,
    });
  };

  return (
    <JobWithRebuild jobId={jobId} onSelectedJobParamChange={onSelectedJobParamChange}>
      <ActionStepper
        handleSubmit={handleSubmit(onSubmit)}
        steps={steps}
        setActiveStepForValidation={setActiveStep}
        isStepFailed={isStepFailed}
        validateStep={() => checkValidationAndSetIsStepFailed(createAnalysisStudioOrder[activeStep])}
        stepWarnings={stepWarnings}
      />
    </JobWithRebuild>
  );
};
